/**
 * @fileoverview Input/output schemas for plot-expander-v1.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 */
export const InputSchema = z.object({
  ...BaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.writtenPlot.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.storyContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.characters.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapters.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.worldElements.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.userMessage.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.planningGenreGuidance.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.projectedWordCount.schema.shape,
  // Calculated fields passed from hook
  scopeGuidance: z.string().optional().describe(
    'Rendered scope guidance from computePlotScopeGuidance — per-section paragraph targets ' +
    'based on projected word count and story form (short/long).'
  ),
  targetParagraphs: z.number().describe('The target number of paragraphs to generate.'),
  expansionMode: z.enum(['bring-to-standard', 'go-beyond-standard']).describe(
    'Which expansion mode to use. "bring-to-standard" fills sparse plot to baseline. ' +
    '"go-beyond-standard" extends adequate plot by ~50%.'
  ),
  isBringToStandard: z.boolean().describe('Convenience flag: true when mode is bring-to-standard.'),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

export const OutputSchema = z.object({
  summary: z.string().describe(
    'A concise overview of the plot (100-150 words) that weaves together the story summary, its central theme, and the main conflict. ' +
    'This summary is displayed as a subtitle on the Active Story Outline card and used as context by other AI agents. ' +
    'Must reflect the expanded plot content.'
  ),
  expandedWrittenPlot: z.string().describe(
    'The expanded plot outline in Markdown format. ' +
    'Use ## headings for each structural phase (Initial State, Inciting Incident, Break Into Two, Midpoint, Break Into Three, Climax, Final State). ' +
    'Use **bold** for character names and key events, *italic* for emotional states. ' +
    'Separate paragraphs with double newlines. Do NOT use HTML tags. ' +
    'Weave together all storylines into a cohesive narrative covering every phase. ' +
    'Write in present tense, third person. ' +
    'The narrative should be verbose and granular enough to guide subsequent chapter planning and prose drafting.'
  ),
  commentary: z.string().optional().describe('Brief explanation of the expansion approach taken.'),
});

export type Output = z.infer<typeof OutputSchema>;
